import { AlertCircle, Mail, Phone, MapPin, Scale } from "lucide-react";
import ReclamiForm from "../components/ReclamiForm";
import DocumentsGrid from "../components/DocumentsGrid";
import { BRAND } from "../data/brand";

export default function Reclami() {
  return (
    <div data-testid="reclami-page">
      {/* Hero */}
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="container-90 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-8">
              <div className="overline mb-6">Reclami · Trasparenza</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.02]">
                Gestione Reclami
              </h1>
            </div>
            <div className="lg:col-span-4">
              <p className="text-lg text-slate-600 leading-relaxed">
                La soddisfazione del cliente è la nostra priorità. Se desideri inviare una
                segnalazione o un reclamo, puoi farlo facilmente tramite i canali indicati di
                seguito.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Info banner */}
      <section className="bg-white border-b border-slate-200">
        <div className="container-90 py-10">
          <div
            data-testid="reclami-info-box"
            className="flex flex-col md:flex-row gap-6 md:items-center bg-[color:var(--brand-blue)] text-white p-8 lg:p-10"
          >
            <div className="flex-shrink-0 w-14 h-14 bg-white/15 flex items-center justify-center">
              <Scale className="w-7 h-7" strokeWidth={1.5} />
            </div>
            <div>
              <div className="overline text-white/70 mb-2">Conformità normativa</div>
              <h2 className="font-heading text-xl lg:text-2xl font-bold leading-snug">
                I reclami saranno gestiti nel rispetto delle normative IVASS vigenti.
              </h2>
              <p className="text-sm text-white/85 leading-relaxed mt-3 max-w-3xl">
                90 Brokers Srls è iscritta al Registro Unico degli Intermediari (RUI). Ogni
                segnalazione viene presa in carico da un responsabile dedicato e gestita
                secondo le procedure previste dalla regolamentazione vigente.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Channels */}
      <section className="section bg-white">
        <div className="container-90">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8">
              <div className="overline mb-3">Modulo di reclamo</div>
              <h2 className="font-heading text-2xl lg:text-3xl font-extrabold text-slate-900 mb-3 tracking-tight leading-tight">
                Compila il modulo per inviare un reclamo.
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8 max-w-2xl">
                Tutti i campi contrassegnati con * sono obbligatori. Puoi allegare documenti a
                supporto della tua segnalazione.
              </p>
              <ReclamiForm />
            </div>

            <aside className="lg:col-span-4 space-y-4">
              <div className="bg-slate-900 text-white p-8" data-testid="reclami-channels">
                <div className="overline text-white/60 mb-4">Canali dedicati ai reclami</div>
                <h3 className="font-heading text-2xl font-bold leading-tight mb-6">
                  Contatti diretti
                </h3>

                <ul className="space-y-5">
                  <li>
                    <div className="text-[0.65rem] uppercase tracking-[0.2em] font-bold text-white/60 mb-1">
                      PEC reclami
                    </div>
                    <a
                      href={`mailto:${BRAND.pec}`}
                      data-testid="reclami-pec-link"
                      className="font-heading text-base font-bold text-white hover:text-[color:var(--brand-blue-light)] break-all flex items-start gap-2"
                    >
                      <Mail className="w-4 h-4 mt-1 flex-shrink-0 text-[color:var(--brand-blue-light)]" />
                      <span>{BRAND.pec}</span>
                    </a>
                  </li>
                  <li>
                    <div className="text-[0.65rem] uppercase tracking-[0.2em] font-bold text-white/60 mb-1">
                      Telefono
                    </div>
                    <a
                      href={`tel:${BRAND.complaintsPhoneRaw}`}
                      data-testid="reclami-phone-link"
                      className="font-heading text-base font-bold text-white hover:text-[color:var(--brand-blue-light)] flex items-start gap-2"
                    >
                      <Phone className="w-4 h-4 mt-1 flex-shrink-0 text-[color:var(--brand-blue-light)]" />
                      <span>{BRAND.complaintsPhone}</span>
                    </a>
                  </li>
                  <li>
                    <div className="text-[0.65rem] uppercase tracking-[0.2em] font-bold text-white/60 mb-1">
                      Sede legale
                    </div>
                    <div
                      data-testid="reclami-address"
                      className="font-heading text-base font-bold text-white flex items-start gap-2"
                    >
                      <MapPin className="w-4 h-4 mt-1 flex-shrink-0 text-[color:var(--brand-blue-light)]" />
                      <span>
                        {BRAND.address.street}
                        <br />
                        03100 {BRAND.address.city} (FR)
                      </span>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 border border-amber-200 p-6 flex gap-3">
                <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-900 leading-relaxed">
                  Ricorda: i reclami via PEC hanno valore legale. Conserva l'avviso di
                  consegna come ricevuta.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Documents area */}
      <DocumentsGrid />
    </div>
  );
}
